import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import LanguageRoute from "./components/LanguageRoute";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import AdminPage from "./pages/AdminPage";
import TeamPage from "./pages/TeamPage";
import FAQPage from "./pages/FAQPage";
import BusinessPage from "./pages/BusinessPage";
import PostPage from "./pages/PostPage";
import AboutPage from "./pages/AboutPage";
import PdfEditorPage from "./pages/PdfEditorPage";
import BgRemoverPage from "./pages/BgRemoverPage";
import TourismSoftwarePage from "./pages/TourismSoftwarePage";
import KeyFlightPage from "./pages/KeyFlightPage";
import ImageResizePage from "./pages/ImageResizePage";
import NotFoundPage from "./pages/NotFoundPage";
import TourPage from "./pages/TourPage";
import StampMakerPage from "./pages/StampMakerPage";
import ResumePage from "./pages/ResumePage";
import ResumeEditorPage from "./pages/ResumeEditorPage";
import VisaStatusPage from "./pages/VisaStatusPage";
import FreelanceVisaPage from "./pages/FreelanceVisaPage";
import PrivacyPage from "./pages/PrivacyPage";
import RealReviewPage from "./pages/RealReviewPage";
import MobileWelcome from "./pages/MobileWelcome";
import AuthCallback from "./components/AuthCallback";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Track visitor for real-time online count
const trackVisitor = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/track-visitor`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (e) {
    // Silently fail
  }
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Check URL fragment for session_id from Google OAuth - must be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Default (English) routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/start-business" element={<BusinessPage />} />
      <Route path="/post" element={<PostPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/tours" element={<TourPage />} />
      <Route path="/client/pdfeditor" element={<PdfEditorPage />} />
      <Route path="/bgremover" element={<BgRemoverPage />} />
      <Route path="/tourismsoftware" element={<TourismSoftwarePage />} />
      <Route path="/keyflight" element={<KeyFlightPage />} />
      <Route path="/imageresize" element={<ImageResizePage />} />
      <Route path="/stampmaker" element={<StampMakerPage />} />
      <Route path="/resume" element={<ResumePage />} />
      <Route path="/resume/editor" element={<ResumeEditorPage />} />
      <Route path="/visa-status" element={<VisaStatusPage />} />
      <Route path="/freelance-visa" element={<FreelanceVisaPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/realreview" element={<RealReviewPage />} />
      <Route path="/reviews" element={<PostPage />} />
      <Route path="/m" element={<MobileWelcome />} />
      <Route path="/app" element={<MobileWelcome />} />

      {/* Language-prefixed routes (ru, uz) for SEO */}
      <Route path="/:lang" element={<LanguageRoute><HomePage /></LanguageRoute>} />
      <Route path="/:lang/login" element={<LanguageRoute><LoginPage /></LanguageRoute>} />
      <Route path="/:lang/register" element={<LanguageRoute><RegisterPage /></LanguageRoute>} />
      <Route path="/:lang/forgot-password" element={<LanguageRoute><ForgotPasswordPage /></LanguageRoute>} />
      <Route path="/:lang/reset-password" element={<LanguageRoute><ResetPasswordPage /></LanguageRoute>} />
      <Route path="/:lang/dashboard" element={<LanguageRoute><DashboardPage /></LanguageRoute>} />
      <Route path="/:lang/team" element={<LanguageRoute><TeamPage /></LanguageRoute>} />
      <Route path="/:lang/faq" element={<LanguageRoute><FAQPage /></LanguageRoute>} />
      <Route path="/:lang/start-business" element={<LanguageRoute><BusinessPage /></LanguageRoute>} />
      <Route path="/:lang/post" element={<LanguageRoute><PostPage /></LanguageRoute>} />
      <Route path="/:lang/about" element={<LanguageRoute><AboutPage /></LanguageRoute>} />
      <Route path="/:lang/tours" element={<LanguageRoute><TourPage /></LanguageRoute>} />
      <Route path="/:lang/bgremover" element={<LanguageRoute><BgRemoverPage /></LanguageRoute>} />
      <Route path="/:lang/tourismsoftware" element={<LanguageRoute><TourismSoftwarePage /></LanguageRoute>} />
      <Route path="/:lang/keyflight" element={<LanguageRoute><KeyFlightPage /></LanguageRoute>} />
      <Route path="/:lang/imageresize" element={<LanguageRoute><ImageResizePage /></LanguageRoute>} />
      <Route path="/:lang/stampmaker" element={<LanguageRoute><StampMakerPage /></LanguageRoute>} />
      <Route path="/:lang/resume" element={<LanguageRoute><ResumePage /></LanguageRoute>} />
      <Route path="/:lang/resume/editor" element={<LanguageRoute><ResumeEditorPage /></LanguageRoute>} />
      <Route path="/:lang/visa-status" element={<LanguageRoute><VisaStatusPage /></LanguageRoute>} />
      <Route path="/:lang/freelance-visa" element={<LanguageRoute><FreelanceVisaPage /></LanguageRoute>} />
      <Route path="/:lang/privacy" element={<LanguageRoute><PrivacyPage /></LanguageRoute>} />
      <Route path="/:lang/realreview" element={<LanguageRoute><RealReviewPage /></LanguageRoute>} />
      <Route path="/:lang/reviews" element={<LanguageRoute><PostPage /></LanguageRoute>} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Track visitor on mount and every 60 seconds
  useEffect(() => {
    trackVisitor();
    const interval = setInterval(trackVisitor, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </div>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
